// Control Center offer builder (Blueprint §4.3.3 / D-5).
// Margin-first: live MarginPanel beside the terms; send is disabled while margin
// is non-positive and requires a reason below 10% (mirrors the server C-1 gate).

import { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { SectionHeader, MarginPanel, marginHealth, computeMargin } from "../../../components/control";
import { Field } from "../../../components/control/Field";
import {
  bccUpsertOfferDraftCallable,
  bccSendDemoProposalCallable,
  bccSendSubscriptionProposalCallable,
} from "../../../lib/bccPhase1Callables";
import { adminGetCommercialSettingsCallable } from "../../../lib/adminControlTowerCallables";

const input = "w-full rounded-xl border border-slate-300 bg-white px-3 py-2 text-sm text-slate-900 focus:border-slate-400 focus:outline-none focus:ring-2 focus:ring-orange-200";

export function ControlOfferBuilderPage() {
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const firmId = params.get("firmId") ?? "";

  const [offerType, setOfferType] = useState<"demo" | "subscription">("subscription");
  const [discountPercent, setDiscountPercent] = useState(0);
  const [demoDurationDays, setDemoDurationDays] = useState(14);
  const [initialCreditGrant, setInitialCreditGrant] = useState(500);
  const [monthlyIncludedCredits, setMonthlyIncludedCredits] = useState(500);
  const [creditCap, setCreditCap] = useState(1000);
  const [marginOverrideReason, setMarginOverrideReason] = useState("");

  const [pricing, setPricing] = useState({ standardPriceAed: 699, blendedAiCostPerCreditAed: 0.04 });
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState("");
  const [error, setError] = useState("");

  useEffect(() => {
    void (async () => {
      try {
        const res = await adminGetCommercialSettingsCallable({});
        setPricing({ standardPriceAed: res.data.planBasePriceAed, blendedAiCostPerCreditAed: res.data.blendedAiCostPerCreditAed });
        setMonthlyIncludedCredits(res.data.defaultMonthlyIncludedCredits ?? 500);
        setCreditCap(res.data.defaultCreditCap ?? 1000);
      } catch {
        /* fall back to defaults */
      }
    })();
  }, []);

  const marginInputs = useMemo(
    () => ({
      standardPriceAed: pricing.standardPriceAed,
      discountPercent,
      monthlyIncludedCredits,
      initialCreditGrant,
      blendedAiCostPerCreditAed: pricing.blendedAiCostPerCreditAed,
    }),
    [pricing, discountPercent, monthlyIncludedCredits, initialCreditGrant],
  );

  const margin = computeMargin(marginInputs);
  const health = marginHealth(margin.grossMarginPercent);
  const sendBlocked = offerType !== "demo" && health === "blocked";
  const needsReason = offerType !== "demo" && health === "critical" && !marginOverrideReason.trim();

  const saveAndSend = useCallback(
    async (send: boolean) => {
      if (!firmId) {
        setError("Open this from a tenant (no firmId). Use Tenants → a firm → New offer.");
        return;
      }
      setBusy(true);
      setError("");
      setMsg("");
      try {
        const saved = await bccUpsertOfferDraftCallable({
          targetFirmId: firmId,
          offerType,
          discountPercent,
          demoDurationDays,
          initialCreditGrant,
          monthlyIncludedCredits,
          creditCap,
        });
        const offerId = String((saved.data.offer as { offerId?: string })?.offerId ?? "");
        if (!send) {
          setMsg("Draft saved.");
          setBusy(false);
          return;
        }
        if (offerType === "demo") {
          await bccSendDemoProposalCallable({ offerId });
        } else {
          await bccSendSubscriptionProposalCallable({ offerId, marginOverrideReason: marginOverrideReason.trim() || undefined });
        }
        setMsg("Proposal sent.");
        setTimeout(() => navigate(`/control/tenants/${firmId}`), 800);
      } catch (err) {
        setError(err instanceof Error ? err.message : "Could not save/send offer.");
      } finally {
        setBusy(false);
      }
    },
    [firmId, offerType, discountPercent, demoDurationDays, initialCreditGrant, monthlyIncludedCredits, creditCap, marginOverrideReason, navigate],
  );

  return (
    <div className="space-y-4">
      <SectionHeader title="New offer" subtitle={firmId ? `Tenant ${firmId}` : "No tenant selected"} />
      {error ? <p className="rounded-lg border border-red-200 bg-red-50 px-3 py-2 text-sm font-semibold text-red-700">{error}</p> : null}
      {msg ? <p className="rounded-lg border border-emerald-200 bg-emerald-50 px-3 py-2 text-sm font-semibold text-emerald-700">{msg}</p> : null}

      <div className="grid grid-cols-1 gap-4 lg:grid-cols-2">
        <div className="space-y-3 rounded-xl border border-slate-200 bg-white p-4">
          <Field label="Offer type">
            <select className={input} value={offerType} onChange={(e) => setOfferType(e.target.value as "demo" | "subscription")}>
              <option value="subscription">Subscription</option>
              <option value="demo">Demo</option>
            </select>
          </Field>
          <Field label="Discount" unit="%"><input type="number" className={input} value={discountPercent} onChange={(e) => setDiscountPercent(Number(e.target.value))} /></Field>
          {offerType === "demo" ? (
            <Field label="Demo duration" unit="days"><input type="number" className={input} value={demoDurationDays} onChange={(e) => setDemoDurationDays(Number(e.target.value))} /></Field>
          ) : null}
          <div className="grid grid-cols-2 gap-3">
            <Field label="Included credits" unit="/mo"><input type="number" className={input} value={monthlyIncludedCredits} onChange={(e) => setMonthlyIncludedCredits(Number(e.target.value))} /></Field>
            <Field label="Initial grant"><input type="number" className={input} value={initialCreditGrant} onChange={(e) => setInitialCreditGrant(Number(e.target.value))} /></Field>
          </div>
          <Field label="Credit cap"><input type="number" className={input} value={creditCap} onChange={(e) => setCreditCap(Number(e.target.value))} /></Field>
          {needsReason ? (
            <Field label="Low-margin reason" error="Required below 10% margin"><input className={input} value={marginOverrideReason} onChange={(e) => setMarginOverrideReason(e.target.value)} /></Field>
          ) : null}
        </div>

        <div className="space-y-3">
          <MarginPanel inputs={marginInputs} />
          <div className="flex gap-2">
            <button type="button" disabled={busy} onClick={() => void saveAndSend(false)} className="rounded-xl border border-slate-200 bg-white px-4 py-2 text-sm font-bold text-slate-700 disabled:opacity-50">Save draft</button>
            <button
              type="button"
              disabled={busy || sendBlocked || needsReason}
              onClick={() => void saveAndSend(true)}
              className="rounded-xl bg-orange-500 px-5 py-2 text-sm font-black text-white disabled:opacity-50"
              title={sendBlocked ? "Margin must be positive to send" : undefined}
            >
              {busy ? "Working…" : "Send proposal"}
            </button>
          </div>
          {sendBlocked ? <p className="text-[11px] font-semibold text-red-600">Send disabled — margin is not positive.</p> : null}
        </div>
      </div>
    </div>
  );
}
